import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';

import Link from 'components/shared/link';

import CalculatorIcon from './images/calculator.inline.svg';
import CheckIcon from './images/check.inline.svg';
import GearIcon from './images/gear.inline.svg';
import LaboratoryFlaskIcon from './images/laboratory-flask.inline.svg';
import MegaphoneInline from './images/megaphone.inline.svg';
import NetworkIcon from './images/network.inline.svg';
import PhoneScreenIcon from './images/phone-screen.inline.svg';
import PieChartIcon from './images/pie-chart.inline.svg';

const icons = {
  calculator: {
    image: CalculatorIcon,
    size: 'size-7 lg:size-5',
  },
  check: {
    image: CheckIcon,
    size: 'size-7',
  },
  gear: {
    image: GearIcon,
    size: 'size-7',
  },
  'laboratory-flask': {
    image: LaboratoryFlaskIcon,
    size: 'size-[26px] xl:size-6 lg:size-[22px] md:size-5',
  },
  megaphone: {
    image: MegaphoneInline,
    size: 'size-7',
  },
  network: {
    image: NetworkIcon,
    size: 'size-7 xl:size-[26px] lg:size-6',
  },
  'phone-screen': {
    image: PhoneScreenIcon,
    size: 'size-[30px] xl:size-[26px] lg:size-6 md:size-[22px]',
  },
  'pie-chart': {
    image: PieChartIcon,
    size: 'size-7 lg:size-5',
  },
};

const cardThemes = {
  blue: {
    background: 'bg-[#C1C9F6]',
    border: 'bg-[linear-gradient(127.06deg,#B8C2E080,#EAEDF600)]',
    iconColor: 'text-secondary-2',
    iconShadow: 'shadow-[0_2px_8px_0px_#5C70D633,0_6px_20px_0_#E6E9F8B2]',
  },
  red: {
    background: 'bg-[#F7D4D4]',
    border: 'bg-[linear-gradient(127.06deg,#E0B8B880,#EAEDF600)]',
    iconColor: 'text-new-primary-1',
    iconShadow: 'shadow-[0_2px_8px_0px_#BF40401F,0_6px_20px_0_#F8E6E699]',
  },
  green: {
    background: 'bg-[#C1F6E0]',
    border: 'bg-[linear-gradient(127.06deg,#B8E0CF80,#EAEDF600)]',
    iconColor: 'text-secondary-1',
    iconShadow: 'shadow-[0_2px_8px_0px_#4CB28829,0_6px_20px_0_#DBF0E799]',
  },
};

const SectionWithColoredCards = ({ title, description, items }) => (
  <section className="section-with-colored-cards relative safe-paddings">
    <div
      className={clsx(
        'container grid grid-cols-12 gap-x-8 lg:gap-x-5 md:max-w-[472px] md:grid-cols-4',
        items.length < 3 && 'lg:max-w-[770px]'
      )}
    >
      {title && (
        <h2 className="fs-56 col-start-3 col-span-8 text-center text-new-black md:col-span-full md:col-start-1">
          {title}
        </h2>
      )}
      {description && (
        <p className="fs-20 col-start-3 col-span-8 mt-5 px-20 text-center text-new-gray-1 xl:mt-4 xl:px-16 lg:mt-3.5 lg:col-start-2 lg:col-span-10 lg:px-11 md:col-span-full md:col-start-1 md:mt-3 md:px-0">
          {description}
        </p>
      )}
      <ul
        className={clsx(
          'flex flex-wrap justify-center col-span-12 gap-8 mt-20 xl:mt-12 md:col-span-full md:col-start-1 md:mt-11',
          items.length < 3
            ? 'md:flex-col lg:gap-6'
            : 'xl:gap-6 lg:col-start-3 lg:col-span-8 lg:flex-col lg:gap-5'
        )}
      >
        {items.map(({ iconName, title, description, linkText, linkUrl }, index) => {
          const { image: Icon, size } = icons[iconName];

          let theme;
          if (index % 3 === 0) theme = cardThemes.blue;
          else if (index % 3 === 1) theme = cardThemes.red;
          else theme = cardThemes.green;

          return (
            <li
              className={clsx(
                'relative w-1/3 max-w-96 min-h-64 rounded-2xl overflow-hidden lg:min-h-40',
                linkText && linkUrl && 'lg:min-h-64 md:!min-h-[273px]',
                linkText && linkUrl && items.length < 3 && 'lg:min-h-52',
                items.length < 3
                  ? 'xl:w-1/2 xl:max-w-1/2 lg:max-w-[48.2%] md:w-full md:max-w-full md:!min-h-60'
                  : 'lg:w-full lg:max-w-full'
              )}
              key={index}
            >
              <div className="h-full border border-[#EAEDF6] rounded-2xl">
                <div
                  className={clsx(
                    'relative z-10 h-full p-6 pt-5 bg-new-gray-2 rounded-[15px] md:py-5',
                    items.length < 3 ? 'lg:p-5 lg:pr-4' : 'lg:px-5 lg:pt-4 lg:pb-[30px]'
                  )}
                >
                  <div className="relative z-10 flex flex-col h-full">
                    <span
                      className={clsx(
                        'flex justify-center items-center size-[60px] bg-white rounded-full lg:size-12',
                        items.length < 3 ? '' : 'xl:size-14',
                        theme.iconShadow
                      )}
                    >
                      <Icon className={clsx(theme.iconColor, size)} />
                    </span>
                    <h3
                      className={clsx(
                        'mt-6 text-new-black md:mt-4',
                        items.length < 3
                          ? 'text-[24px] leading-tight tracking-tight-4 font-medium lg:mt-[18px] lg:text-[20px] md:pt-0.5'
                          : 'fs-28 xl:mt-5'
                      )}
                    >
                      {title}
                    </h3>
                    <p
                      className={clsx(
                        'pr-2 mb-auto font-light text-[16px] text-new-gray-1 xl:pr-0',
                        items.length < 3
                          ? 'mt-2 xl:pr-2 lg:mt-1.5 lg:pr-0.5 lg:text-[14px]'
                          : 'mt-3.5 xl:mt-2.5'
                      )}
                    >
                      {description}
                    </p>
                    {linkText && linkUrl && (
                      <Link
                        className={clsx(
                          '!text-[16px] md:!text-[14px]',
                          items.length < 3 ? 'mt-7 lg:mt-6 lg:!text-[14px]' : 'mt-9 xl:mt-5 lg:mt-4'
                        )}
                        to={linkUrl}
                        theme="secondary-2"
                        size="md"
                        withArrow
                      >
                        {linkText}
                        <span className="sr-only">about {title}</span>
                      </Link>
                    )}
                  </div>
                  <span
                    className={clsx(
                      'absolute -top-32 -left-32 w-[400px] h-[356px] rounded-full opacity-15 blur-3xl xl:-top-40 xl:-left-40',
                      theme.background
                    )}
                  />
                </div>
                <span className={clsx('absolute -inset-px rounded-2xl', theme.border)} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  </section>
);

SectionWithColoredCards.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      iconName: PropTypes.oneOf(Object.keys(icons)).isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      linkText: PropTypes.string,
      linkUrl: PropTypes.string,
    })
  ).isRequired,
};

SectionWithColoredCards.defaultProps = {
  title: null,
  description: null,
};

export default SectionWithColoredCards;
